import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Pencil, Save, X, Plus, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";

const MAX_ATTACHMENTS = 3;
const MAX_SIZE = 10 * 1024 * 1024;

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });

function AuthImage({ src, alt, token, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    axios.get(src, authHeaders(token)).then((res) => {
      objectUrl = URL.createObjectURL(res.data);
      if (!cancelled) setUrl(objectUrl);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, token]);
  if (!url) return <div className="h-20 w-20 rounded border border-slate-200 bg-slate-200 animate-pulse" />;
  return <img src={url} alt={alt} className={className} />;
}

async function openWithAuth(src, token, contentType, filename, download) {
  try {
    const res = await axios.get(src, authHeaders(token));
    const blob = new Blob([res.data], { type: contentType || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    if (download) {
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "file");
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } else {
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  } catch {
    toast.error("Failed to open attachment");
  }
}

function PostTimestamps({ post }) {
  return (
    <p className="text-xs text-slate-500 mt-1.5">
      Posted by <b>{post.author}</b> on {new Date(post.created_date).toLocaleString("en-AU")}
      {post.edited_date && (
        <span className="text-amber-600"> • Edited on {new Date(post.edited_date).toLocaleString("en-AU")}</span>
      )}
    </p>
  );
}

function AttachmentList({ attachments, attachmentUrl, onRemove, authToken }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="post-attachments">
      {attachments.map((att) => {
        const isImage = (att.content_type || "").startsWith("image/");
        return (
          <div key={att.id} className="relative group">
            {isImage ? (
              authToken ? (
                <button type="button" onClick={() => openWithAuth(attachmentUrl(att, true), authToken, att.content_type, att.filename, false)} data-testid={`attachment-image-${att.id}`}>
                  <AuthImage src={attachmentUrl(att, true)} alt={att.filename} token={authToken} className="h-20 w-20 object-cover rounded border border-slate-200 hover:opacity-80" />
                </button>
              ) : (
                <a href={attachmentUrl(att, true)} target="_blank" rel="noreferrer" data-testid={`attachment-image-${att.id}`}>
                  <img src={attachmentUrl(att, true)} alt={att.filename} className="h-20 w-20 object-cover rounded border border-slate-200 hover:opacity-80" />
                </a>
              )
            ) : authToken ? (
              <button
                type="button"
                onClick={() => openWithAuth(attachmentUrl(att, false), authToken, att.content_type, att.filename, true)}
                className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 hover:bg-blue-100 max-w-[180px]"
                data-testid={`attachment-file-${att.id}`}
              >
                <FileText className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{att.filename}</span>
              </button>
            ) : (
              <a
                href={attachmentUrl(att, false)}
                className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 hover:bg-blue-100 max-w-[180px]"
                data-testid={`attachment-file-${att.id}`}
              >
                <FileText className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{att.filename}</span>
              </a>
            )}
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(att.id)}
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none"
                title="Remove attachment"
                data-testid={`attachment-remove-${att.id}`}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FilePicker({ files, setFiles, remaining, testId }) {
  const inputRef = useRef(null);
  const pick = (e) => {
    const chosen = Array.from(e.target.files || []);
    const valid = [];
    for (const f of chosen) {
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name} is larger than 10MB`);
        continue;
      }
      valid.push(f);
    }
    const merged = [...files, ...valid].slice(0, remaining + files.length);
    if (files.length + valid.length > remaining + files.length) toast.error(`Maximum ${MAX_ATTACHMENTS} attachments per post`);
    setFiles(merged);
    e.target.value = "";
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input ref={inputRef} type="file" multiple className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={remaining <= 0} data-testid={testId}>
        <Paperclip className="w-4 h-4 mr-1" /> Attach
      </Button>
      {files.map((f, i) => (
        <span key={`${f.name}-${i}`} className="flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1">
          <span className="truncate max-w-[140px]">{f.name}</span>
          <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-red-500 font-bold" data-testid={`remove-pending-file-${i}`}>×</button>
        </span>
      ))}
    </div>
  );
}

export function ProgressPosts({ posts, viewerKey, readOnly, onAdd, onEdit, attachmentUrl, authToken }) {
  const [newText, setNewText] = useState("");
  const [newFiles, setNewFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editFiles, setEditFiles] = useState([]);
  const [keptAttachments, setKeptAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const submitNew = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    const ok = await onAdd(newText, newFiles);
    setSaving(false);
    if (ok) {
      setNewText("");
      setNewFiles([]);
    }
  };

  const startEdit = (post) => {
    setEditingId(post.id);
    setEditText(post.text);
    setKeptAttachments(post.attachments || []);
    setEditFiles([]);
  };

  const submitEdit = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    const ok = await onEdit(editingId, editText, editFiles, keptAttachments.map((a) => a.id));
    setSaving(false);
    if (ok) {
      setEditingId(null);
      setEditText("");
      setEditFiles([]);
      setKeptAttachments([]);
    }
  };

  return (
    <div className="space-y-3" data-testid="progress-posts">
      {(posts || []).length === 0 && <p className="text-sm text-slate-500">No progress updates yet.</p>}
      {(posts || []).map((post) => {
        const isMine = !readOnly && viewerKey && post.author_key === viewerKey;
        const isEditing = editingId === post.id;
        return (
          <div key={post.id} data-testid={`progress-post-${post.id}`}>
            {isEditing ? (
              <div className="space-y-2">
                <Textarea rows={4} value={editText} onChange={(e) => setEditText(e.target.value)} data-testid={`progress-post-edit-input-${post.id}`} />
                <AttachmentList attachments={keptAttachments} attachmentUrl={attachmentUrl} authToken={authToken} onRemove={(id) => setKeptAttachments(keptAttachments.filter((a) => a.id !== id))} />
                <FilePicker files={editFiles} setFiles={setEditFiles} remaining={MAX_ATTACHMENTS - keptAttachments.length - editFiles.length} testId="edit-attach-button" />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingId(null)} data-testid={`progress-post-cancel-${post.id}`}>
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                  <Button size="sm" onClick={submitEdit} disabled={saving} data-testid={`progress-post-save-${post.id}`}>
                    <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className={`rounded-lg border p-3 ${isMine ? "bg-white border-blue-200" : "bg-slate-100 border-slate-200"}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm whitespace-pre-wrap flex-1 ${isMine ? "text-slate-800" : "text-slate-600"}`}>{post.text}</p>
                  {isMine && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => startEdit(post)} title="Edit your note" data-testid={`progress-post-edit-${post.id}`}>
                      <Pencil className="w-4 h-4 text-blue-600" />
                    </Button>
                  )}
                </div>
                <AttachmentList attachments={post.attachments} attachmentUrl={attachmentUrl} authToken={authToken} />
                <PostTimestamps post={post} />
              </div>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <div className="pt-3 border-t space-y-2">
          <Textarea
            rows={3}
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Write a new progress update..."
            data-testid="new-progress-input"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <FilePicker files={newFiles} setFiles={setNewFiles} remaining={MAX_ATTACHMENTS - newFiles.length} testId="new-attach-button" />
            <Button size="sm" onClick={submitNew} disabled={saving || !newText.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="new-progress-add-button">
              <Plus className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Add Update"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
